import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";
import useUserProfile from "@/hooks/useUserProfile";
import { ApiError } from "@/lib/api";
import { getCountry } from "@/lib/countries";
import { getLanguage } from "@/lib/languages";
import { getState } from "@/lib/states";
import type { UserProfile } from "@/lib/profile-api";
import {
  generateRuleDiff,
  type RuleDiffRequestDto,
  type RuleDiffResponseDto,
} from "@/lib/rulediff-api";

/**
 * apps/web :: components/RuleDiffForm
 *
 * Authoring surface for the rule-diff preview page. Four fields +
 * submit button; renders the generated diff below on success, or an
 * inline error message on failure.
 *
 * The component intentionally does NO client-side validation beyond
 * "every field has a value" — the backend is the source of truth and
 * owns the empty-summary 400 + bad-request-shape 400 paths.
 *
 * Default policy (ROADS-372): for an authenticated learner whose
 * `/api/profile` resolves, the form pre-populates from the profile —
 * `home_country` → country display name, `current_focus_state` (or first
 * `target_states` entry) → US-state display name, `native_language`
 * (LangCode) → option-value matching the four-language `<select>` (with
 * English as the safe fallback when the learner's native language isn't
 * one of the four hard-coded options yet).
 *
 * For anonymous users, OR for authenticated users whose profile request
 * 404s / errors, the form falls back to the legacy
 * `India / California / Telugu` defaults — that pair matches the first
 * benchmark fixture so a cold-click "Generate" still hits the known-pair
 * template.
 *
 * Loading-state policy: while the profile is in flight we render a
 * skeleton instead of the form. Painting the form with stale defaults
 * and then mutating them once the profile resolves both (a) erodes
 * first-session trust ("why does it think I'm Indian?") and (b) trips
 * paint-sensitive axe rules like target-size when measurements run
 * before the final layout settles. See durable memory:
 * `feedback_axe_paint_sensitive_rules_need_networkidle.md`.
 */

const FALLBACK_HOME_COUNTRY = "India";
const FALLBACK_TARGET_STATE = "California";
const FALLBACK_TARGET_LANGUAGE = "Telugu";

/** The four <select> options the form ships with today. Keep in sync. */
const SUPPORTED_LANGUAGE_OPTIONS = ["Telugu", "Spanish", "Russian", "English"] as const;
type SupportedLanguageOption = (typeof SUPPORTED_LANGUAGE_OPTIONS)[number];

interface FormDefaults {
  homeCountry: string;
  targetState: string;
  targetLanguage: SupportedLanguageOption;
}

const FALLBACK_DEFAULTS: FormDefaults = {
  homeCountry: FALLBACK_HOME_COUNTRY,
  targetState: FALLBACK_TARGET_STATE,
  targetLanguage: FALLBACK_TARGET_LANGUAGE,
};

/**
 * Map a stored {@link UserProfile} onto pre-fill values for the form.
 *
 * Country: backend stores ISO alpha-2 (`"IN"`, `"MX"`, …). Convert via
 * the catalog so the prompt sees `"India"` / `"Mexico"`. Falls through
 * to the raw code if the catalog misses (we shouldn't get here — the
 * server validates against an allow-list — but it's a cheap safety net).
 *
 * State: prefer `current_focus_state`, fall back to first `target_states`
 * entry, then to `California` if neither is set. Resolve to the display
 * name (`"Texas"`) the same way countries do.
 *
 * Language: backend stores `LangCode` (`"te"`, `"es"`, …). Convert to
 * the English name (`"Telugu"`, `"Spanish"`) so it matches one of the
 * four `<option value="…">` strings the select renders. If the learner's
 * native language isn't one of the four (Hindi, Tagalog, Ukrainian, or
 * Simplified Chinese today), fall back to English — that's the only
 * safe choice; we'd rather render English than mismatch the option list.
 */
function pickDefaultsFromProfile(profile: UserProfile): FormDefaults {
  const country = getCountry(profile.home_country);
  const homeCountry = country?.name ?? profile.home_country;

  const stateCode = profile.current_focus_state ?? profile.target_states[0] ?? null;
  const stateMeta = stateCode ? getState(stateCode) : undefined;
  const targetState = stateMeta?.name ?? stateCode ?? FALLBACK_TARGET_STATE;

  const lang = getLanguage(profile.native_language);
  const englishName = lang?.englishName;
  const targetLanguage: SupportedLanguageOption =
    englishName != null && (SUPPORTED_LANGUAGE_OPTIONS as readonly string[]).includes(englishName)
      ? (englishName as SupportedLanguageOption)
      : "English";

  return { homeCountry, targetState, targetLanguage };
}

/**
 * Resolve the form defaults given the auth + profile state. Centralised
 * so the render-vs-skeleton decision is one boolean.
 */
function resolveDefaults(
  authKind: "loading" | "anonymous" | "authenticated",
  profileLoading: boolean,
  profile: UserProfile | null,
): { ready: boolean; defaults: FormDefaults } {
  if (authKind === "loading") return { ready: false, defaults: FALLBACK_DEFAULTS };
  if (authKind === "anonymous") return { ready: true, defaults: FALLBACK_DEFAULTS };
  // authenticated
  if (profileLoading) return { ready: false, defaults: FALLBACK_DEFAULTS };
  if (profile == null) return { ready: true, defaults: FALLBACK_DEFAULTS };
  return { ready: true, defaults: pickDefaultsFromProfile(profile) };
}

export default function RuleDiffForm() {
  const { t } = useTranslation();
  const { state: authState } = useAuth();
  const isAuthenticated = authState.kind === "authenticated";
  const profileState = useUserProfile();

  // For anonymous users we don't need (or want) the profile fetch — but
  // useUserProfile() always fires it. That's fine in practice because
  // /rule-diff/preview is wrapped by ProtectedRoute, so this branch only
  // matters for unit tests + future public surfaces. The fetch will 401
  // and surface as `error`; the resolveDefaults() branch above ignores
  // the profile result for anonymous users, so no UX impact.
  const { ready, defaults } = resolveDefaults(
    authState.kind,
    isAuthenticated ? profileState.loading : false,
    isAuthenticated ? profileState.profile : null,
  );

  const [homeCountry, setHomeCountry] = useState(FALLBACK_HOME_COUNTRY);
  const [targetState, setTargetState] = useState(FALLBACK_TARGET_STATE);
  const [targetLanguage, setTargetLanguage] = useState<string>(
    FALLBACK_TARGET_LANGUAGE,
  );
  const [homeRuleSummary, setHomeRuleSummary] = useState("");
  // Track whether we've applied the profile-derived defaults yet — once
  // the user has typed into a field we must NOT clobber their input on a
  // late re-render.
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  useEffect(() => {
    if (!ready || defaultsApplied) return;
    setHomeCountry(defaults.homeCountry);
    setTargetState(defaults.targetState);
    setTargetLanguage(defaults.targetLanguage);
    setDefaultsApplied(true);
  }, [ready, defaults, defaultsApplied]);

  const [status, setStatus] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ok"; response: RuleDiffResponseDto }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setStatus({ kind: "loading" });
    const req: RuleDiffRequestDto = {
      homeCountry,
      targetState,
      targetLanguage,
      homeRuleSummary,
    };
    try {
      const response = await generateRuleDiff(req);
      setStatus({ kind: "ok", response });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
          ? err.message
          : "unknown error";
      setStatus({ kind: "error", message });
    }
  }

  if (!ready) {
    // Skeleton — same outer shape as the form so the layout doesn't
    // jump when the real form swaps in. role="status" + aria-live so AT
    // users hear "loading" and not silence.
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="rule-diff-form-loading"
        className="flex flex-col gap-md rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
      >
        <span className="sr-only">{t("ruleDiff.loading")}</span>
        <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-200" aria-hidden="true" />
        <div className="h-9 w-full animate-pulse rounded bg-neutral-100" aria-hidden="true" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-200" aria-hidden="true" />
        <div className="h-9 w-full animate-pulse rounded bg-neutral-100" aria-hidden="true" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-200" aria-hidden="true" />
        <div className="h-9 w-full animate-pulse rounded bg-neutral-100" aria-hidden="true" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-200" aria-hidden="true" />
        <div className="h-24 w-full animate-pulse rounded bg-neutral-100" aria-hidden="true" />
        <div className="h-9 w-32 animate-pulse rounded bg-neutral-200" aria-hidden="true" />
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="rule-diff-form"
      className="flex flex-col gap-md rounded-lg border border-brand-primary/10 bg-white p-lg shadow-sm sm:p-xl"
    >
      <label className="flex flex-col gap-xs">
        <span className="text-sm font-semibold text-neutral-700">
          {t("ruleDiff.homeCountryLabel")}
        </span>
        <input
          data-testid="rule-diff-home-country"
          type="text"
          value={homeCountry}
          onChange={(ev) => setHomeCountry(ev.target.value)}
          className="rounded-md border border-brand-primary/20 px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          required
        />
      </label>

      <label className="flex flex-col gap-xs">
        <span className="text-sm font-semibold text-neutral-700">
          {t("ruleDiff.targetStateLabel")}
        </span>
        <input
          data-testid="rule-diff-target-state"
          type="text"
          value={targetState}
          onChange={(ev) => setTargetState(ev.target.value)}
          className="rounded-md border border-brand-primary/20 px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          required
        />
      </label>

      <label className="flex flex-col gap-xs">
        <span className="text-sm font-semibold text-neutral-700">
          {t("ruleDiff.targetLanguageLabel")}
        </span>
        <select
          data-testid="rule-diff-target-language"
          value={targetLanguage}
          onChange={(ev) => setTargetLanguage(ev.target.value)}
          className="rounded-md border border-brand-primary/20 px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          required
        >
          <option value="Telugu">Telugu</option>
          <option value="Spanish">Spanish</option>
          <option value="Russian">Russian</option>
          <option value="English">English</option>
        </select>
      </label>

      <label className="flex flex-col gap-xs">
        <span className="text-sm font-semibold text-neutral-700">
          {t("ruleDiff.homeRuleSummaryLabel")}
        </span>
        <textarea
          data-testid="rule-diff-home-rule-summary"
          value={homeRuleSummary}
          onChange={(ev) => setHomeRuleSummary(ev.target.value)}
          rows={4}
          className="rounded-md border border-brand-primary/20 px-sm py-xs text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          required
        />
      </label>

      <button
        data-testid="rule-diff-submit"
        type="submit"
        disabled={status.kind === "loading"}
        className="self-start rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {status.kind === "loading"
          ? t("ruleDiff.submitting")
          : t("ruleDiff.submit")}
      </button>

      {status.kind === "loading" && (
        <div
          role="status"
          aria-live="polite"
          data-testid="rule-diff-loading"
          className="text-sm text-neutral-600"
        >
          {t("ruleDiff.loading")}
        </div>
      )}

      {status.kind === "error" && (
        <div
          role="alert"
          data-testid="rule-diff-error"
          className="rounded-md border border-red-500/40 bg-red-50 px-md py-sm text-sm text-red-800"
        >
          {t("ruleDiff.errorPrefix")}: {status.message}
        </div>
      )}

      {status.kind === "ok" && (
        <section
          aria-labelledby="rule-diff-output-heading"
          className="rounded-md border border-brand-primary/10 bg-brand-surface/40 p-md"
        >
          <h2
            id="rule-diff-output-heading"
            className="font-display text-base font-bold text-brand-primary"
          >
            {t("ruleDiff.outputTitle")}
          </h2>
          <p className="mt-xs text-xs text-neutral-600">
            {t("ruleDiff.providerLabel")}: {status.response.providerId} ·{" "}
            {status.response.latencyMs}ms
          </p>
          <pre
            data-testid="rule-diff-output"
            className="mt-sm whitespace-pre-wrap break-words text-sm text-neutral-900"
          >
            {status.response.diff}
          </pre>
        </section>
      )}
    </form>
  );
}
